import React from 'react';
import { Clock, BarChart2 } from 'lucide-react';

/**
 * Компонент секции выбора графика работы
 */
const WorkScheduleSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  workScheduleTypes,
  shiftPatterns
}) => {
  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <BarChart2 className="w-5 h-5 mr-2 text-cyan-500" />
        График работы
      </h3>
      
      {/* Тип графика работы */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Тип графика работы
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {workScheduleTypes.map((type) => (
            <label 
              key={type.id} 
              className={`
                relative flex flex-col p-3 border rounded-md cursor-pointer
                ${formData.workScheduleType === type.id 
                  ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/30 dark:border-cyan-600' 
                  : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                }
              `}
            >
              <input
                type="radio"
                name="workScheduleType"
                value={type.id}
                checked={formData.workScheduleType === type.id}
                onChange={handleInputChange}
                className="sr-only"
              />
              <span className="text-sm font-medium text-gray-900 dark:text-white">{type.name}</span>
              <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">{type.description}</span>
              <span 
                className={`
                  absolute -top-1 -right-1 w-4 h-4 rounded-full border border-white dark:border-gray-800
                  ${formData.workScheduleType === type.id 
                    ? 'bg-cyan-500' 
                    : 'bg-gray-200 dark:bg-gray-600'
                  }
                `}
              ></span>
            </label>
          ))}
        </div>
      </div>
      
      {/* Параметры стандартного графика (5/2) */}
      {formData.workScheduleType === 'standard' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="workDaysPerWeek" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Рабочих дней в неделю
            </label>
            <input
              type="number"
              id="workDaysPerWeek"
              name="workDaysPerWeek"
              value={formData.workDaysPerWeek}
              onChange={handleInputChange}
              min="1"
              max="7"
              className={`form-input w-full ${errors.workDaysPerWeek ? 'input-error' : ''}`}
            />
            {errors.workDaysPerWeek && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.workDaysPerWeek}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Стандартный график - 5 дней (пн-пт)
            </p>
          </div>
          
          <div>
            <label htmlFor="workHoursPerDay" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Рабочих часов в день
            </label>
            <input
              type="number"
              id="workHoursPerDay"
              name="workHoursPerDay"
              value={formData.workHoursPerDay}
              onChange={handleInputChange}
              min="1"
              max="24"
              step="0.5"
              className={`form-input w-full ${errors.workHoursPerDay ? 'input-error' : ''}`}
            />
            {errors.workHoursPerDay && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.workHoursPerDay}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Стандартный рабочий день - 8 часов
            </p>
          </div>
        </div>
      )}
      
      {/* Параметры сменного графика */}
      {formData.workScheduleType === 'shift' && (
        <div className="space-y-4">
          {/* Шаблон смены */}
          <div>
            <label htmlFor="shiftPattern" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Шаблон смены
            </label>
            <select
              id="shiftPattern"
              name="shiftPattern"
              value={formData.shiftPattern}
              onChange={handleInputChange}
              className="form-select w-full md:w-1/2"
            >
              {shiftPatterns.map((pattern) => (
                <option key={pattern.id} value={pattern.id}>
                  {pattern.name} - {pattern.description}
                </option>
              ))}
            </select>
          </div>
          
          {/* Количество часов в смену */}
          <div>
            <label htmlFor="workHoursPerDay" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Рабочих часов в смену
            </label>
            <input
              type="number"
              id="workHoursPerDay"
              name="workHoursPerDay"
              value={formData.workHoursPerDay}
              onChange={handleInputChange}
              min="1"
              max="24"
              step="0.5"
              className={`form-input w-full md:w-1/2 ${errors.workHoursPerDay ? 'input-error' : ''}`}
            />
            {errors.workHoursPerDay && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.workHoursPerDay}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Параметры произвольного графика */}
      {formData.workScheduleType === 'custom' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="workDaysPerWeek" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Рабочих дней в неделю
            </label>
            <input
              type="number"
              id="workDaysPerWeek"
              name="workDaysPerWeek"
              value={formData.workDaysPerWeek}
              onChange={handleInputChange}
              min="1"
              max="7"
              className={`form-input w-full ${errors.workDaysPerWeek ? 'input-error' : ''}`}
            />
            {errors.workDaysPerWeek && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.workDaysPerWeek}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="workHoursPerDay" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Рабочих часов в день
            </label>
            <input
              type="number"
              id="workHoursPerDay"
              name="workHoursPerDay"
              value={formData.workHoursPerDay}
              onChange={handleInputChange}
              min="1"
              max="24"
              step="0.5"
              className={`form-input w-full ${errors.workHoursPerDay ? 'input-error' : ''}`}
            />
            {errors.workHoursPerDay && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.workHoursPerDay}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Включение перерывов */}
      <div className="mt-4">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeBreaks"
            checked={formData.includeBreaks}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Учитывать перерывы
          </span>
        </label>
        
        {formData.includeBreaks && (
          <div className="mt-2 ml-6">
            <label htmlFor="breakTimeMinutes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Продолжительность перерыва (минут в день)
            </label>
            <input
              type="number"
              id="breakTimeMinutes"
              name="breakTimeMinutes"
              value={formData.breakTimeMinutes}
              onChange={handleInputChange}
              min="0"
              max="240"
              className={`form-input w-full md:w-1/2 ${errors.breakTimeMinutes ? 'input-error' : ''}`}
            />
            {errors.breakTimeMinutes && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.breakTimeMinutes}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Стандартный перерыв - 60 минут (1 час)
            </p>
          </div>
        )}
      </div>
      
      {/* Включение праздников */}
      <div className="mt-4">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeHolidays"
            checked={formData.includeHolidays}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Учитывать праздничные дни
          </span>
        </label>
        
        {formData.includeHolidays && (
          <div className="mt-2 ml-6">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="useProductionCalendar"
                checked={formData.useProductionCalendar}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Использовать производственный календарь РФ
              </span>
            </label>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Учитываются все государственные праздники и перенесенные выходные дни
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkScheduleSection;